import type { CSSProperties } from 'react';
import { useCacheMetrics } from '../data/useCacheMetrics';

interface CachePanelProps {
  current: number;
  count: number;
}

/**
 * Live cache status: usage bar (blue → amber near budget → red over budget), a
 * per-frame residency strip (which of the 50 frames are in memory), and
 * hit/miss counters.
 */
export function CachePanel({ current, count }: CachePanelProps) {
  const m = useCacheMetrics();
  const ready = new Set(m.readyIndices);
  const loading = new Set(m.loadingIndices);
  const pct = Math.min(100, m.ratio * 100);
  const barColor = m.over ? '#ff5c5c' : m.warn ? '#ffcf33' : '#4f9cff';

  return (
    <div className="cache-panel" style={panel}>
      <div style={title}>Frame Cache</div>
      <div style={usageRow}>
        <span>
          {m.usageMb.toFixed(1)} / {m.limitMb} MB
        </span>
        <span style={{ opacity: 0.6 }}>{m.entryCount} frames</span>
      </div>

      <div style={track}>
        <div style={{ ...fill, width: `${pct}%`, background: barColor }} />
      </div>

      {m.over ? (
        <div style={{ color: '#ff5c5c', marginTop: 4 }}>⚠ over budget — evicting</div>
      ) : m.warn ? (
        <div style={{ color: '#ffcf33', marginTop: 4 }}>⚠ approaching budget</div>
      ) : null}

      {/* Residency strip: one cell per frame. */}
      <div style={strip}>
        {Array.from({ length: count }, (_, i) => (
          <span key={i} style={cell(i === current, loading.has(i), ready.has(i))} />
        ))}
      </div>

      <div style={statsRow}>
        <span>hits {m.hits}</span>
        <span>misses {m.misses}</span>
      </div>
    </div>
  );
}

function cell(current: boolean, isLoading: boolean, isReady: boolean): CSSProperties {
  const bg = current
    ? '#ffffff'
    : isLoading
      ? '#ffcf33'
      : isReady
        ? '#4f9cff'
        : 'rgba(255,255,255,0.12)';
  return { flex: 1, height: 7, borderRadius: 1, background: bg };
}

const panel: CSSProperties = {
  position: 'fixed',
  top: 16,
  right: 16,
  width: 240,
  padding: '10px 12px',
  borderRadius: 8,
  background: 'rgba(12,16,22,0.72)',
  color: '#e6edf3',
  font: '12px/1.5 system-ui, sans-serif',
  backdropFilter: 'blur(6px)',
  border: '1px solid rgba(255,255,255,0.08)',
  userSelect: 'none',
};

const title: CSSProperties = { fontWeight: 600, fontSize: 13, marginBottom: 6 };
const usageRow: CSSProperties = { display: 'flex', justifyContent: 'space-between', fontVariantNumeric: 'tabular-nums' };
const track: CSSProperties = {
  marginTop: 6,
  height: 8,
  borderRadius: 4,
  background: 'rgba(255,255,255,0.1)',
  overflow: 'hidden',
};
const fill: CSSProperties = { height: '100%', transition: 'width 120ms ease, background 200ms ease' };
const strip: CSSProperties = { display: 'flex', gap: 1, marginTop: 8 };
const statsRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: 6,
  opacity: 0.7,
  fontVariantNumeric: 'tabular-nums',
};
